import { ref, computed, watch } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import type {
  RegistrationFormData,
  FieldAnswer,
  Registration,
  StoreRegistrationPayload,
} from '@/types/registration'
import { RegistrationStatus } from '@/types/registration'
import type { VoucherValidationResult } from '@/types/voucher'
import {
  fetchRegistrationFormData as apiFetchRegistrationFormData,
  submitRegistration as apiSubmitRegistration,
} from '@/api/registration'
import { validateVoucher as apiValidateVoucher } from '@/api/vouchers'

const registrationData = ref<RegistrationFormData | null>(null)
const currentStep = ref<number>(0)
const fieldAnswers = ref<Map<number, FieldAnswer>>(new Map())
const isSubmitting = ref<boolean>(false)
const submitError = ref<string | null>(null)
const registration = ref<Registration | null>(null)
const loadError = ref<string | null>(null)
const isLoading = ref<boolean>(false)

// Voucher state
const voucherCode = ref<string | null>(null)
const voucherDiscount = ref<number>(0)
const isVoucherLoading = ref<boolean>(false)
const voucherError = ref<string | null>(null)

const totalSteps: ComputedRef<number> = computed(() => {
  const pages = registrationData.value?.wizard_form?.pages?.length ?? 0
  return 1 + pages + 1 // account + custom pages + payment
})

const isWaitingListMode: ComputedRef<boolean> = computed(() => {
  // A promoted participant has status payment_pending — they must pay, not join the waiting list.
  if (registration.value?.status === RegistrationStatus.PaymentPending) return false
  return registrationData.value?.race.is_waiting_list_active ?? false
})

const stepLabels: ComputedRef<string[]> = computed(() => {
  const pages = registrationData.value?.wizard_form?.pages ?? []
  const finalStepLabel = isWaitingListMode.value ? 'Waiting List' : 'Payment'
  return [
    'Account',
    ...pages.map((p) => p.title),
    finalStepLabel,
  ]
})

const selectedOptionsCost: ComputedRef<number> = computed(() => {
  if (!registrationData.value?.wizard_form) return 0

  let total = 0
  const pages = registrationData.value.wizard_form.pages ?? []

  for (const page of pages) {
    const fields = page.fields ?? []
    for (const field of fields) {
      const answer = fieldAnswers.value.get(field.id)
      if (!answer) continue

      // Option-based fields (checkbox_group, radio_group, select)
      if (field.options) {
        const selectedIds = answer.selected_options ?? []
        for (const option of field.options) {
          if (selectedIds.includes(option.id)) {
            total += parseFloat(option.extra_cost)
          }
        }
      }

      // Single checkbox with a field-level extra_cost
      if (field.type === 'checkbox' && field.extra_cost !== null && answer.value === '1') {
        total += parseFloat(field.extra_cost)
      }
    }
  }

  return total
})

const totalAmount: ComputedRef<number> = computed(() => {
  const base = (registrationData.value?.race.pricing.current_price_cents ?? 0) / 100
  const total = base + selectedOptionsCost.value - voucherDiscount.value
  return Math.max(0, Math.round(total * 100) / 100)
})

const isZeroPayment: ComputedRef<boolean> = computed(() => totalAmount.value === 0)

const isFirstStep: ComputedRef<boolean> = computed(() => currentStep.value === 0)

const isLastStep: ComputedRef<boolean> = computed(
  () => currentStep.value === totalSteps.value - 1,
)

const isPaymentStep: ComputedRef<boolean> = computed(
  () => !isWaitingListMode.value && currentStep.value === totalSteps.value - 1,
)

const isWaitingListConfirmStep: ComputedRef<boolean> = computed(
  () => isWaitingListMode.value && currentStep.value === totalSteps.value - 1,
)

async function fetchRegistrationData(orgSlug: string, eventSlug: string, raceSlug: string): Promise<void> {
  isLoading.value = true
  loadError.value = null
  try {
    registrationData.value = await apiFetchRegistrationFormData(orgSlug, eventSlug, raceSlug)
  } catch {
    loadError.value = 'common.error_generic'
  } finally {
    isLoading.value = false
  }
}

function setFieldAnswer(
  wizardFieldId: number,
  value: string | null,
  selectedOptions?: number[],
): void {
  fieldAnswers.value.set(wizardFieldId, {
    wizard_field_id: wizardFieldId,
    value,
    selected_options: selectedOptions,
  })
}

function nextStep(): void {
  if (currentStep.value < totalSteps.value - 1) {
    currentStep.value++
  }
}

function previousStep(): void {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

function extractSubmitErrorMessage(e: unknown): string {
  if (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  ) {
    const response = (
      e as {
        response: {
          status: number
          data: { message?: string; errors?: Record<string, string[]> }
        }
      }
    ).response

    if (response.data?.errors) {
      const firstMessages = Object.values(response.data.errors)[0]
      if (firstMessages?.[0]) return firstMessages[0]
    }
    if (response.data?.message) return response.data.message
  }
  return 'common.error_generic'
}

async function applyVoucher(code: string, raceId: number): Promise<void> {
  isVoucherLoading.value = true
  voucherError.value = null
  try {
    const result: VoucherValidationResult = await apiValidateVoucher({
      code,
      race_id: raceId,
      options_total: selectedOptionsCost.value,
    })
    if (result.valid && result.discount_amount) {
      voucherCode.value = result.voucher?.code ?? code.toUpperCase()
      voucherDiscount.value = parseFloat(result.discount_amount)
    } else {
      voucherError.value = result.message ?? 'registration.voucher.invalid'
    }
  } catch (e: unknown) {
    if (
      typeof e === 'object' &&
      e !== null &&
      'response' in e
    ) {
      const response = (e as { response: { data?: { reason?: string; message?: string } } }).response
      const reason = response.data?.reason
      if (reason) {
        voucherError.value = `registration.voucher.error_${reason}`
      } else {
        voucherError.value = response.data?.message ?? 'registration.voucher.invalid'
      }
    } else {
      voucherError.value = 'registration.voucher.invalid'
    }
  } finally {
    isVoucherLoading.value = false
  }
}

function removeVoucher(): void {
  voucherCode.value = null
  voucherDiscount.value = 0
  voucherError.value = null
}

// Re-validate the applied voucher whenever options change so the discount
// always reflects the current options total (relevant for applies_to=registration_and_options).
watch(selectedOptionsCost, () => {
  const code = voucherCode.value
  const raceId = registrationData.value?.race.id
  if (code !== null && raceId !== undefined) {
    void applyVoucher(code, raceId)
  }
})

async function submitRegistration(raceId: number): Promise<void> {
  isSubmitting.value = true
  submitError.value = null
  try {
    const answers: FieldAnswer[] = Array.from(fieldAnswers.value.values())
    const payload: StoreRegistrationPayload = {
      race_id: raceId,
      field_answers: answers,
      voucher_code: voucherCode.value,
    }
    registration.value = await apiSubmitRegistration(payload)
    nextStep()
  } catch (e: unknown) {
    submitError.value = extractSubmitErrorMessage(e)
  } finally {
    isSubmitting.value = false
  }
}

function resumeFromRegistration(existing: Registration): void {
  registration.value = existing
  currentStep.value = totalSteps.value - 1
}

function resetState(): void {
  registrationData.value = null
  currentStep.value = 0
  fieldAnswers.value = new Map()
  isSubmitting.value = false
  submitError.value = null
  registration.value = null
  loadError.value = null
  isLoading.value = false
  voucherCode.value = null
  voucherDiscount.value = 0
  isVoucherLoading.value = false
  voucherError.value = null
}

export function useRegistration(): {
  registrationData: Ref<RegistrationFormData | null>
  currentStep: Ref<number>
  fieldAnswers: Ref<Map<number, FieldAnswer>>
  isSubmitting: Ref<boolean>
  submitError: Ref<string | null>
  registration: Ref<Registration | null>
  isLoading: Ref<boolean>
  loadError: Ref<string | null>
  totalSteps: ComputedRef<number>
  stepLabels: ComputedRef<string[]>
  selectedOptionsCost: ComputedRef<number>
  totalAmount: ComputedRef<number>
  isFirstStep: ComputedRef<boolean>
  isLastStep: ComputedRef<boolean>
  isPaymentStep: ComputedRef<boolean>
  isWaitingListMode: ComputedRef<boolean>
  isWaitingListConfirmStep: ComputedRef<boolean>
  isZeroPayment: ComputedRef<boolean>
  voucherCode: Ref<string | null>
  voucherDiscount: Ref<number>
  isVoucherLoading: Ref<boolean>
  voucherError: Ref<string | null>
  fetchRegistrationData: (orgSlug: string, eventSlug: string, raceSlug: string) => Promise<void>
  setFieldAnswer: (wizardFieldId: number, value: string | null, selectedOptions?: number[]) => void
  nextStep: () => void
  previousStep: () => void
  submitRegistration: (raceId: number) => Promise<void>
  resumeFromRegistration: (existing: Registration) => void
  resetState: () => void
  applyVoucher: (code: string, raceId: number) => Promise<void>
  removeVoucher: () => void
} {
  return {
    registrationData,
    currentStep,
    fieldAnswers,
    isSubmitting,
    submitError,
    registration,
    isLoading,
    loadError,
    totalSteps,
    stepLabels,
    selectedOptionsCost,
    totalAmount,
    isFirstStep,
    isLastStep,
    isPaymentStep,
    isWaitingListMode,
    isWaitingListConfirmStep,
    isZeroPayment,
    voucherCode,
    voucherDiscount,
    isVoucherLoading,
    voucherError,
    fetchRegistrationData,
    setFieldAnswer,
    nextStep,
    previousStep,
    submitRegistration,
    resumeFromRegistration,
    resetState,
    applyVoucher,
    removeVoucher,
  }
}
